import { makeStyles, tokens } from "@coreai/fluentui-react";
import { thinScrollbarStyles, customScrollbarStyles } from "@/styles/scrollbar";

export const useOperationPickerPanelStyles = makeStyles({
  panel: {
    position: "absolute",
    top: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground2,
    backdropFilter: "blur(20px) saturate(1.5)",
    WebkitBackdropFilter: "blur(20px) saturate(1.5)",
    borderTop: "none",
    borderRight: "none",
    borderBottom: "none",
    borderLeft: `1px solid ${tokens.colorNeutralStroke2}`,
    boxShadow: tokens.shadow16,
    minWidth: "400px",
    maxWidth: "720px",
    overflow: "visible",
    zIndex: 20,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  headerTitle: {
    flex: 1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
    margin: 0,
  },
  searchContainer: {
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground1,
    flexShrink: 0,
  },
  searchInput: {
    width: "100%",
  },
  content: {
    flex: 1,
    minHeight: 0, // Critical: allows flex child to shrink and scroll
    overflowY: "auto",
    // Top padding lives on inner content (e.g. `searchGroupList`) instead
    // of here — `position: sticky; top: 0` resolves to the inner edge of
    // the scroll container's padding box, so a top padding here would
    // leave a gap above sticky connector headers where operations are
    // visible scrolling past.
    paddingTop: 0,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: tokens.spacingHorizontalS,
    ...thinScrollbarStyles,
  },
  contentVirtualized: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalS,
    paddingRight: 0,
    overflow: "hidden", // Let FixedSizeList handle scrolling
  },
  section: {
    marginBottom: tokens.spacingVerticalM,
  },
  sectionHeader: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXS,
  },
  categoriesList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  operationsList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    gap: tokens.spacingVerticalM,
  },
  askOttoCard: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: "pointer",
    transitionProperty: "background-color, border-color",
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
    width: "100%",
    maxWidth: "240px",
    "&:hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      border: `1px solid ${tokens.colorBrandStroke1}`,
    },
  },
  askOttoIcon: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase600,
  },
  askOttoLabel: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  askOttoHint: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
  },
  loadingState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    gap: tokens.spacingVerticalM,
  },
  loadingMoreHint: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    paddingTop: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  connectorSections: {
    display: "flex",
    flexDirection: "column",
  },
  toolbarRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: tokens.spacingVerticalS,
  },
  connectorCount: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  expandCollapseButtons: {
    display: "flex",
    gap: tokens.spacingHorizontalXS,
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    margin: 0,
    marginTop: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXS,
  },
  virtualizedHeaderRow: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXS}`,
  },
  virtualizedConnectorRow: {
    paddingBottom: tokens.spacingVerticalXS,
  },
  virtualizedFavoriteRow: {
    paddingBottom: tokens.spacingVerticalS,
  },
  virtualizedList: {
    ...customScrollbarStyles,
  },
  collapsibleSectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalXS,
    paddingRight: tokens.spacingHorizontalS,
    backgroundColor: "transparent",
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    width: "100%",
    textAlign: "left",
    font: "inherit",
    transitionProperty: "background-color",
    transitionDuration: tokens.durationFaster,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "1px",
    },
  },
  collapsibleSectionIcon: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground3,
    transitionProperty: "transform",
    transitionDuration: tokens.durationFast,
    flexShrink: 0,
  },
  collapsibleSectionIconExpanded: {
    transform: "rotate(90deg)",
  },
  collapsibleSectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  collapsibleSectionCount: {
    marginLeft: "auto",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Connector-grouped search results. The visual chrome (border, header,
  // operations list with brand-color accent) is provided by the embedded
  // ConnectorSection so the search view matches the browse view.
  searchGroupList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    // Breathing room above the first card. Lives here (not on the scroll
    // container) so sticky connector headers can stick flush with the
    // scroll viewport top — see note on `content` above.
    paddingTop: tokens.spacingVerticalS,
  },
  // Hide the search results list while a connector drill-in is active so
  // we can keep the cards mounted (preserving their state and avoiding a
  // costly remount on "Back to results") instead of swapping subtrees.
  searchGroupListHidden: {
    display: "none",
  },
  searchViewAllButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalXS,
    width: "100%",
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalXXS,
    backgroundColor: "transparent",
    border: "none",
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorBrandForeground1,
    cursor: "pointer",
    textAlign: "left",
    borderRadius: tokens.borderRadiusSmall,
    ":hover": {
      color: tokens.colorBrandForeground2Hover,
      textDecoration: "underline",
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorBrandStroke1}`,
      outlineOffset: "1px",
    },
  },
  searchDetailContainer: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  searchDetailBack: {
    alignSelf: "flex-start",
  },
});

